import { useEffect } from 'react';
import App from '@gral/app';

import act from '@flux/actions/actView';
import { Switch } from '@components/Inputs';
import AddressesListAdd from '../Addresses/AddressesListAdd';
import ContactData from '../ContactData/ContactData';
import ItemImagesTab from '@pages/DataBase/Documents/DocumentTab';
import AvatarImage from '@components/Entities/AvatarImage';
import WrapperEntitySelector from '@modules/entitySelector/WrapperEntitySelector';
import Tasks, { defaultConfigComponent as TConfigComponent } from '@pages/DataBase/Tasks/Tasks';
import Labels, { defaultConfigComponent as LConfigComponent } from '@pages/DataBase/Configuration/Labels/Labels';
import { useSetState } from 'react-use';
import { useAppDispatch, useAppSelector } from '@redux/store';
import { GridContainerFullHeight, TextInput, Tabs } from '@ts/storybook';
import Misc from '@components/Misc';
import PersonDataAccordion from '@modules/accordions/PersonDataAccordion';
import { globalActions } from '@redux/slices/states/global/globalSlice';
import { CUser, EAclAction, IForm } from '@ts/ui-models';
import { Button, Grid, Modal, ChangePasswordForm, Typography } from '@ts/storybook';
import DataGridParameterSettings from '../Parameters/DataGridParameterSettings';
import { useRouterContext } from '@utils/context/RouterContext';

interface IUserFormProps extends IForm {
  value: CUser;
}

const UserForm = (props: IUserFormProps) => {
  const { transferPropsRoute } = useRouterContext();
  const { values: tUser, onCallbackChange, mode } = props;
  const [state, setState] = useSetState<{
    showModalPwd: boolean;
    pwdStr: null;
    activeTab: string;
    passwordDefinition: string;
    passwordOperation: string | null;
  }>({
    showModalPwd: false,
    pwdStr: null,
    activeTab: 'information',
    passwordDefinition: 'manual',
    passwordOperation: null,
  });
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state.global.user);
  const loggedUserId = user?.id;
  const isAdmin = useAppSelector(state => state.global.user?.role.isAdmin);
  const _userId = transferPropsRoute.params.id != null ? transferPropsRoute.params.id : 'new';

  const targetUserId = tUser.getID();
  const isViewMode = mode === 'view';
  const isNewMode = mode === 'new';

  //#region Handlers
  const handleTabChange = (_event, tab) => {
    return setState({ activeTab: tab });
  };

  const onChangeTrim = attr => value => {
    let valueTrim = value.trim();
    props.onChange(attr, valueTrim);
  };

  const onChangeName = (name: string | number | null) => {
    onCallbackChange('login')(name);
    onCallbackChange('customId')(name);
  };

  const shouldDisableSubmitPasswordOperation = () => {
    const { passwordOperation, passwordDefinition } = state;

    const { currentPassword, password, passwordConfirmation, passwordDefinition: passwordDefinitionValue } = tUser;
    const passwordMatch = password === passwordConfirmation;

    if (passwordDefinition === 'manual' && passwordMatch) {
      if (currentPassword || isNewMode) return false;
    } else if (passwordDefinitionValue === 'generated') {
      return !currentPassword && !isNewMode ? true : false;
    } else {
      if (passwordOperation === 'reset' && !isNewMode) return false;
    }

    return true;
  };

  const handleChangePwd = () => {
    act.update({
      url: `/api/user/${tUser.getID()}/changePassword`,
      dataPath: `changePassword`,
      attrs: {
        password: tUser.currentPassword,
        newPassword: tUser.password,
        sendPwdToUser: tUser.sendPwdToUser,
        sendPwdToCreator: tUser.sendPwdToCreator,
        askUserChangePwd: tUser.askUserChangePwd,
      },
    });
    closeModalPwd();
  };

  const handleResetPwd = () => {
    act.update({
      url: `/api/user/${tUser.id}/resetPassword`,
      dataPath: `resetPassword`,
    });
    closeModalPwd();
  };

  const handleClearImage = () => {
    props.onChange(
      'documents',
      tUser.documents?.filter(image => image.purposeId !== 'default')
    );
  };

  const handleUploadPhotoSuccess = image => {
    let documents = tUser.documents?.filter(doc => doc.purposeId !== 'default');
    documents?.push(image);
    props.onChange('documents', documents);
    if (loggedUserId === targetUserId) {
      dispatch(globalActions.updateGlobalState({ key: 'user', value: { ...user, defaultImage: image.locator } }));
    }
  };
  //#endregion

  //#region Functions
  const closeModalPwd = () => {
    setState({ showModalPwd: false });
  };
  //#endregion

  //#region Render
  const renderSettings = () => {
    return <DataGridParameterSettings {...props} type="user" />;
  };

  const renderContents = () => {
    const contents = (() => {
      switch (state.activeTab) {
        case 'information':
          return renderInformation();
        case 'contactData':
          return renderContactData();
        case 'tasks':
          return renderTasks();
        case 'settings':
          return renderSettings();
        case 'documents':
          return renderDocumentsTab();
      }
    })();

    return contents;
  };

  const renderInformation = () => {
    const { requiredFields } = props;

    return (
      <Misc.NewPaperWithPadding>
        <Grid container spacing={2} p={2} alignItems="flex-start">
          <Grid container spacing={2} size={{ sm: 6 }} direction={'column'}>
            <TextInput
              isReadOnly={isViewMode}
              label="User Login ID"
              placeholder="Unique identifier"
              value={tUser.login}
              onChangeValue={onChangeName}
              required={requiredFields.includes('login')}
            />
            <TextInput
              isReadOnly={isViewMode}
              label="Custom ID"
              value={tUser.customId}
              onChangeValue={onCallbackChange('customId')}
              infoText="Blank spaces at the beggining and the end will be trimmed on save"
              type="ID"
            />
            <TextInput
              isReadOnly={isViewMode}
              label="Notifications Email"
              value={tUser.email}
              onChangeValue={onChangeTrim('email')}
              required={requiredFields.includes('email')}
            />
            <PersonDataAccordion {...props} linkToPerson isViewMode={isViewMode} personData={tUser.getPersonData()} />
            {((isViewMode && tUser.tagIds?.length) || !isViewMode) && (
              <WrapperEntitySelector
                label="Labels"
                value={tUser.tagIds}
                onChange={onCallbackChange('tagIds')}
                searchComponent={{
                  component: Labels,
                  configComponent: { ...LConfigComponent, where: { restrictedToEntities: 'taUser' } },
                }}
                multiple
                isReadOnly={isViewMode}
              />
            )}
            {!(!tUser.notes && isViewMode) && (
              <TextInput
                isReadOnly={isViewMode}
                label={'Notes'}
                value={tUser.notes}
                multiline
                onChangeValue={onCallbackChange('notes')}
              />
            )}
          </Grid>
          <Grid container size={{ sm: 6 }} justifyContent="center" alignItems="flex-start">
            <Grid size={{ xs: 12 }} mb={6}>
              {renderPhotoBox()}
            </Grid>
            <Switch
              disabled={isViewMode}
              checked={tUser.isEnabled ?? true}
              onChangeValue={onCallbackChange('isEnabled')}
              leftLabel={'Enabled'}
              tooltip="Enable or disable this user, if disabled, the user will not be able to login"
            />
            <Grid container size={{ xs: 12 }} justifyContent="center" alignItems="start">
              {renderPwdOperation()}
            </Grid>
          </Grid>
          {renderModal()}
        </Grid>
      </Misc.NewPaperWithPadding>
    );
  };

  const renderDocumentsTab = () => {
    return (
      <ItemImagesTab
        {...props}
        //documents={tUser.documents?.filter(doc => doc.purposeId !== 'default')}
        //entityId={_userId}
        entityTypeId={'taUser'}
      />
    );
  };

  const renderContactData = () => {
    return (
      <Grid container spacing={2} sx={{ width: '99%' }}>
        <Grid size={{ xs: 12 }}>
          <ContactData
            {...Object.assign({}, props, {
              type: 'user',
              xTypeId: _userId,
              entity: 'taUser',
            })}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <AddressesListAdd
            {...Object.assign({}, props, {
              type: 'user',
              xTypeId: _userId,
              entity: 'taUser',
            })}
          />
        </Grid>
      </Grid>
    );
  };

  const renderTasks = () => {
    return (
      <GridContainerFullHeight>
        <Tasks configComponent={{ ...TConfigComponent, where: { responsibleUserId: _userId } }} />
      </GridContainerFullHeight>
    );
  };

  const renderPwdOperation = () => {
    const { passwordOperation } = state;
    const itself = loggedUserId === targetUserId;

    if (isNewMode && !App.cxStore.checkPermission('taUser', EAclAction.CREATE)) return;
    if (mode === 'view') return;
    if (mode === 'edit' && !itself && (!isAdmin || (isAdmin && !App.cxStore.checkPermission('taUser', EAclAction.EDIT)))) return;

    const btnCaption = isNewMode ? 'New password' : passwordOperation == 'change' ? 'Change password' : 'Reset user password';

    return (
      <Button
        variant="outlined"
        className="mt-8"
        onClick={() => {
          setState({ showModalPwd: true });
        }}
        disabled={isViewMode}
        label={btnCaption}
      />
    );
  };

  const renderModal = () => {
    let renderContent, handler;
    if (isNewMode) {
      renderContent = renderChangePasswordContent;
      handler = closeModalPwd;
    } else if (state.passwordOperation == 'change') {
      renderContent = renderChangePasswordContent;
      handler = handleChangePwd;
    } else {
      renderContent = renderResetPasswordContent;
      handler = handleResetPwd;
    }

    return (
      <Modal
        withHeader
        title="Change password"
        labelOk={isNewMode ? 'Add' : 'Submit'}
        labelCancel="Close"
        fullWidth
        maxWidth="lg"
        onClose={closeModalPwd}
        open={state.showModalPwd}
        onSubmit={handler}
        disabled={shouldDisableSubmitPasswordOperation()}
      >
        <Grid container size={{ xs: 12 }} padding={2}>
          {renderContent()}
        </Grid>
      </Modal>
    );
  };

  const renderChangePasswordContent = () => {
    return <ChangePasswordForm {...props} />;
  };

  const renderResetPasswordContent = () => {
    const { login } = tUser;

    return (
      <div>
        <Typography variant="body1" text={['Are you sure you want to reset the password for user', ':', login, '?']} />
      </div>
    );
  };

  const renderPhotoBox = () => {
    return (
      <div style={{ paddingTop: '20px' }}>
        <AvatarImage
          mode={props.mode}
          cEntity={tUser}
          image={tUser.getAvatar()}
          onUploadedPhoto={handleUploadPhotoSuccess}
          onClearImage={handleClearImage}
          entityTypeId="taUser"
          entityId={tUser.getID()}
        />
      </div>
    );
  };

  //#endregion

  const tabs = [
    { label: 'Information', value: 'information' },
    {
      label: 'Documents',
      value: 'documents',
    },
    {
      label: 'Contact data',
      value: 'contactData',
      disabled: isNewMode,
    },
    {
      label: 'Tasks',
      value: 'tasks',
      disabled: isNewMode,
    },

    {
      label: 'Settings',
      value: 'settings',
      disabled: isNewMode,
    },
  ];

  useEffect(() => {
    const isCurrentUser = loggedUserId === targetUserId;

    let passwordOperation: string | null = null;

    if (isCurrentUser) passwordOperation = 'change';
    else if (isAdmin) passwordOperation = 'reset';

    setState({ passwordOperation });
  }, []);

  const fTabs = isNewMode ? tabs.slice(0, 2) : tabs;

  return (
    <Tabs id={'form-user'} tabs={fTabs} value={state.activeTab} onChange={handleTabChange}>
      {renderContents()}
    </Tabs>
  );
};

export default UserForm;
